import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import QRTicketDialog from "@/components/QRTicketDialog";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const MAX_POLLS = 12;       // 12 × 2s = ~24s total
const POLL_INTERVAL = 2000;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [state, setState] = useState({ phase: "polling", booking: null, message: "Confirming your payment…" });
  const [open, setOpen] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!sessionId) { setState({ phase: "error", message: "Missing session." }); return; }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" && data.booking) {
          setState({ phase: "paid", booking: data.booking, message: "Payment successful." });
          setOpen(true);
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", message: "The Stripe checkout session expired." });
          return;
        }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState({ phase: "timeout", message: "Still processing — your ticket will appear in My Bookings shortly." });
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      } catch (e) {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState({ phase: "error", message: "Couldn't verify payment. Please check My Bookings." });
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, loading, user]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-md px-5 md:px-8 py-16 text-center" data-testid="booking-success-page">
        {state.phase === "polling" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin" />
            <h1 className="font-display text-2xl font-extrabold mt-4">Confirming your payment</h1>
            <p className="text-sm text-muted-foreground mt-2">{state.message}</p>
            <p className="text-[11px] text-muted-foreground mt-1 font-mono">{sessionId}</p>
          </>
        )}
        {state.phase === "paid" && state.booking && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-primary" />
            <h1 className="font-display text-3xl font-black mt-4">You're in!</h1>
            <p className="text-sm text-muted-foreground mt-1">Your e-ticket is ready.</p>
            <div className="mt-6 space-y-3">
              <Button onClick={() => setOpen(true)} className="rounded-full w-full" data-testid="view-ticket-btn">View ticket</Button>
              <Button variant="outline" onClick={() => navigate("/bookings")} className="rounded-full w-full">Go to My Bookings</Button>
            </div>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <Clock className="h-12 w-12 mx-auto text-primary" />
            <h1 className="font-display text-2xl font-extrabold mt-4">Almost there</h1>
            <p className="text-sm text-muted-foreground mt-2">{state.message}</p>
            <Button onClick={() => navigate("/bookings")} className="rounded-full mt-4">My Bookings</Button>
          </>
        )}
        {(state.phase === "expired" || state.phase === "error") && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-destructive" />
            <h1 className="font-display text-2xl font-extrabold mt-4">{state.phase === "expired" ? "Session expired" : "Couldn't verify"}</h1>
            <p className="text-sm text-muted-foreground mt-2">{state.message}</p>
            <Button onClick={() => navigate("/")} className="rounded-full mt-4">Back home</Button>
          </>
        )}
      </main>
      <QRTicketDialog open={open} onOpenChange={setOpen} booking={state.booking} />
    </div>
  );
}
